import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { createFileRoute, Link } from "@tanstack/react-router";
import NewTestTemplateDialog from "@/features/harvest-new-test-template-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Archive,
  ArchiveRestore,
  MoreHorizontal,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute("/analytics/templates/")({
  component: () => (
    <RequireCapability cap="test-template:edit">
      <RouteComponent />
    </RequireCapability>
  ),
});

type ConfirmTarget =
  | { kind: "archive"; id: number; name: string }
  | { kind: "unarchive"; id: number; name: string };

function RouteComponent() {
  const utils = trpc.useUtils();
  const canEdit = useCan("test-template:edit");
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmTarget | null>(null);

  const { data: templates, isLoading } = trpc.tests.testTemplates.list.useQuery(
    { includeArchived: showArchived },
  );

  const invalidate = () => utils.tests.testTemplates.list.invalidate();

  const archive = trpc.tests.testTemplates.archive.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Template archived.");
    },
    onError: (err) => toast.error(err.message),
  });
  const unarchive = trpc.tests.testTemplates.unarchive.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });

  const filtered = useMemo(() => {
    if (!templates) return [];
    const lower = searchQuery.toLowerCase().trim();
    if (!lower) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(lower) ||
        (t.description ?? "").toLowerCase().includes(lower) ||
        (t.code ?? "").toLowerCase().includes(lower) ||
        (t.category ?? "").toLowerCase().includes(lower),
    );
  }, [templates, searchQuery]);

  const runConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === "archive") archive.mutate(confirm.id as never);
    else unarchive.mutate(confirm.id as never);
    setConfirm(null);
  };

  if (isLoading || !templates) {
    return (
      <div className="container mx-auto px-4 py-6">Loading templates...</div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      <div>
        <h1 className="text-lg font-semibold">Test Templates</h1>
        <p className="text-sm text-muted-foreground">
          Manage test templates for quality analysis.
        </p>
      </div>

      <div className="flex items-center gap-1 mt-3 mb-3 border-b border-olive-6">
        <button
          className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${!showArchived ? "border-olive-12 text-olive-12" : "border-transparent text-olive-9 hover:text-olive-11"}`}
          onClick={() => setShowArchived(false)}
        >
          Active
        </button>
        <button
          className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${showArchived ? "border-olive-12 text-olive-12" : "border-transparent text-olive-9 hover:text-olive-11"}`}
          onClick={() => setShowArchived(true)}
        >
          Archived
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, description, code, category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {canEdit && <NewTestTemplateDialog />}
      </div>

      {confirm && (
        <div className="mb-3 px-3 py-2 border border-amber-400 bg-amber-50 flex items-center justify-between">
          <span className="text-sm">
            {confirm.kind === "archive"
              ? `Archive ${confirm.name}? New tests can't be created from it.`
              : `Unarchive ${confirm.name}?`}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="destructive" size="sm" onClick={runConfirm}>
              Confirm
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirm(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="divide-y divide-border">
        {filtered.map((t) => (
          <div
            key={t.id}
            className="flex items-center justify-between px-3 py-3 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Link
                to="/analytics/templates/$template_id"
                params={{ template_id: t.id }}
                className="text-sm font-medium text-primary hover:underline"
              >
                {t.name}
              </Link>
              {t.code && (
                <span className="text-xs font-mono text-olive-11">
                  {t.code}
                </span>
              )}
              {t.category && (
                <span className="text-xs text-olive-11 truncate">
                  {t.category}
                </span>
              )}
              {t.description && (
                <span className="text-xs text-muted-foreground truncate">
                  {t.description}
                </span>
              )}
              {t.archived_at && (
                <span className="text-xs text-olive-9 italic">archived</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {t.archived_at ? (
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          setConfirm({
                            kind: "unarchive",
                            id: t.id,
                            name: t.name,
                          });
                        }}
                      >
                        <ArchiveRestore className="h-3.5 w-3.5 mr-2" />
                        Unarchive
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          setConfirm({
                            kind: "archive",
                            id: t.id,
                            name: t.name,
                          });
                        }}
                      >
                        <Archive className="h-3.5 w-3.5 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && !searchQuery && (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-3 text-sm">
            No test templates found.
          </p>
          {canEdit && <NewTestTemplateDialog />}
        </div>
      )}
    </div>
  );
}
